import { useEffect, useRef } from 'react';
import { SelectionAllIcon, SquareIcon, TrashIcon } from '@phosphor-icons/react';
import { T } from 'gt-react';
import { ZipTieMenuItems } from './ZipTieOverlay';

/**
 * What the user right-clicked, which decides which items the menu offers.
 */
export type CanvasContextMenuTarget =
  /**
   * Empty canvas. `enclosingGroup` is set when the click happened to land inside
   * a layout box: the box's interior is `pointer-events: none` so the pane gets
   * the click, which would otherwise make "delete this box" reachable only by
   * right-clicking its label.
   */
  | {
      kind: 'pane';
      enclosingGroup?: { groupId: string; label: string };
    }
  | { kind: 'selection'; selectedCount: number }
  | { kind: 'stepLayoutGroup'; groupId: string; label: string }
  | { kind: 'zipTie'; tieId: string };

export interface CanvasContextMenuState {
  /** Screen coordinates of the click. */
  x: number;
  y: number;
  target: CanvasContextMenuTarget;
}

interface CanvasContextMenuProps {
  state: CanvasContextMenuState;
  onClose: () => void;
  onCreateGroup: () => void;
  onGroupSelection: () => void;
  onDeleteGroup: (groupId: string) => void;
  /** Zip-tie actions. Canvases without zip ties never open a `zipTie` menu. */
  onRecolorZipTie?: (tieId: string, color: string | undefined) => void;
  onRemoveZipTie?: (tieId: string) => void;
}

/**
 * Right-click menu for the workflow canvas.
 *
 * Positioned absolutely at the click rather than anchored to a trigger, since
 * the "trigger" is a point on an SVG canvas. Follows the same pattern as the
 * workflow-list row context menu.
 */
export function CanvasContextMenu({
  state,
  onClose,
  onCreateGroup,
  onGroupSelection,
  onDeleteGroup,
  onRecolorZipTie,
  onRemoveZipTie,
}: CanvasContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleDismiss(event: MouseEvent | KeyboardEvent) {
      if (event instanceof KeyboardEvent) {
        if (event.key === 'Escape') onClose();
        return;
      }
      // Clicks *inside* the menu must not dismiss it. This has to be a
      // containment check, not `stopPropagation` on the menu: the listener below
      // is registered on `window` in the capture phase, so it runs before the
      // item's own handler — dismissing here would unmount the menu and the
      // click would never reach the button.
      const { target } = event;
      if (
        target instanceof globalThis.Node &&
        menuRef.current?.contains(target)
      )
        return;
      onClose();
    }
    // Capture phase so a click anywhere — including on the React Flow pane,
    // which stops propagation — closes the menu.
    window.addEventListener('pointerdown', handleDismiss, true);
    window.addEventListener('keydown', handleDismiss, true);
    return () => {
      window.removeEventListener('pointerdown', handleDismiss, true);
      window.removeEventListener('keydown', handleDismiss, true);
    };
  }, [onClose]);

  const { target } = state;
  // Deleting a box is offered both from the box's own menu (its label) and from
  // the pane menu when the click landed inside one, so the action is reachable
  // wherever the user actually clicked.
  const deletableGroup =
    target.kind === 'stepLayoutGroup'
      ? { groupId: target.groupId, label: target.label }
      : target.kind === 'pane' && target.enclosingGroup
        ? target.enclosingGroup
        : null;

  return (
    <div
      ref={menuRef}
      role="menu"
      className="fixed z-50 min-w-52 overflow-hidden rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md"
      style={{ left: state.x, top: state.y }}
    >
      {target.kind === 'selection' && (
        <MenuItem
          icon={<SelectionAllIcon className="h-4 w-4" />}
          onClick={() => {
            onGroupSelection();
            onClose();
          }}
        >
          <T context="workflow canvas context menu action that wraps the selected steps in a group">
            Group Nodes
          </T>
          <span className="ml-1 text-xs text-muted-foreground">
            ({target.selectedCount})
          </span>
        </MenuItem>
      )}

      {target.kind === 'zipTie' && (
        <ZipTieMenuItems
          onRecolor={(color) => {
            onRecolorZipTie?.(target.tieId, color);
            onClose();
          }}
          onRemove={() => {
            onRemoveZipTie?.(target.tieId);
            onClose();
          }}
        />
      )}

      {(target.kind === 'pane' || target.kind === 'selection') && (
        <MenuItem
          icon={<SquareIcon className="h-4 w-4" />}
          onClick={() => {
            onCreateGroup();
            onClose();
          }}
        >
          <T context="workflow canvas context menu action that creates an empty step layout group box">
            Create layout group
          </T>
        </MenuItem>
      )}

      {deletableGroup && (
        <MenuItem
          icon={<TrashIcon className="h-4 w-4" />}
          onClick={() => {
            onDeleteGroup(deletableGroup.groupId);
            onClose();
          }}
        >
          <T context="workflow canvas context menu action that removes a step layout group box but keeps its steps">
            Delete layout group (keep steps)
          </T>
          {deletableGroup.label.length > 0 && (
            <span className="ml-1 max-w-32 truncate text-xs text-muted-foreground">
              {deletableGroup.label}
            </span>
          )}
        </MenuItem>
      )}
    </div>
  );
}

function MenuItem({
  icon,
  onClick,
  children,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
    >
      {icon}
      {children}
    </button>
  );
}
